import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react'
import {
  formatCurrencyFromCents,
  type CeramicPackage,
} from '../../data/protection-catalog'
import { commitSelectedProtection } from '../../lib/commit-selected-protection'
import {
  browseCarousel,
  carouselPlacement,
  featuredPackageIndex,
  protectionCarouselOptions,
} from '../../lib/protection-carousel'
import {
  buildProtectionBookHref,
  buildProtectionQuoteHref,
  commitsProtectionToBookingRoute,
  readSelectedProtection,
  toSelectedProtection,
} from '../../lib/selected-protection'
import { useCeramicPackages } from '../../lib/use-ceramic-packages'
import { Button, ButtonLink } from '../ui/button'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '../ui/carousel'

export interface ProtectionSelectorProps {
  packages?: CeramicPackage[]
  variant?: 'full' | 'preview'
  showContinueActions?: boolean
  onSelect?: (item: CeramicPackage) => void
}

export function ProtectionSelector({
  packages: packagesProp,
  variant = 'full',
  showContinueActions,
  onSelect,
}: Readonly<ProtectionSelectorProps>) {
  const loadedPackages = useCeramicPackages()
  const navigate = useNavigate()
  const packages = packagesProp ?? loadedPackages
  const statusId = useId()
  const isPreview = variant === 'preview'
  const continueActions = showContinueActions ?? !isPreview
  const navigateOnCommit = commitsProtectionToBookingRoute({
    variant,
    showContinueActions,
  })
  const startIndex = featuredPackageIndex(packages)
  const carouselOpts = useMemo(() => protectionCarouselOptions(), [])
  const [carouselApi, setCarouselApi] = useState<CarouselApi>()
  const [activePackageIndex, setActivePackageIndex] = useState(startIndex)
  const draggedSincePointerDown = useRef(false)
  const startedCarousel = useRef(false)
  const [selectedId, setSelectedId] = useState<string | null>(
    () => readSelectedProtection()?.id ?? null,
  )

  useEffect(() => {
    const storedId = readSelectedProtection()?.id
    if (storedId && packages.some((item) => item.id === storedId)) {
      setSelectedId(storedId)
    }
  }, [packages])

  useEffect(() => {
    if (!carouselApi || startedCarousel.current) return
    startedCarousel.current = true
    carouselApi.scrollTo(startIndex, true)
  }, [carouselApi, startIndex])

  useEffect(() => {
    if (!carouselApi) return

    const syncCentredSlide = (api: CarouselApi) => {
      if (!api) return
      setActivePackageIndex(api.selectedScrollSnap())
    }

    let pointerIsDown = false
    const markPointerDown = () => {
      pointerIsDown = true
      draggedSincePointerDown.current = false
    }
    const markDragged = () => {
      if (pointerIsDown) draggedSincePointerDown.current = true
    }
    const markPointerUp = () => {
      pointerIsDown = false
    }

    syncCentredSlide(carouselApi)
    carouselApi.on('select', syncCentredSlide)
    carouselApi.on('reInit', syncCentredSlide)
    carouselApi.on('pointerDown', markPointerDown)
    carouselApi.on('pointerUp', markPointerUp)
    carouselApi.on('scroll', markDragged)

    return () => {
      carouselApi.off('select', syncCentredSlide)
      carouselApi.off('reInit', syncCentredSlide)
      carouselApi.off('pointerDown', markPointerDown)
      carouselApi.off('pointerUp', markPointerUp)
      carouselApi.off('scroll', markDragged)
    }
  }, [carouselApi])

  const selectedPackage = packages.find((item) => item.id === selectedId) ?? null
  const centredPackage = packages[activePackageIndex] ?? packages[0]

  function showPackageAt(index: number) {
    carouselApi?.scrollTo(index, true)
  }

  function activatePackageCard(index: number) {
    if (draggedSincePointerDown.current) return
    showPackageAt(index)
  }

  function selectPackage(item: CeramicPackage, index: number) {
    commitSelectedProtection(item, undefined, { newBookingIntent: navigateOnCommit })
    setSelectedId(item.id)
    showPackageAt(index)
    onSelect?.(item)
    if (navigateOnCommit) {
      void navigate({ to: '/book', search: { protection: item.id } as never })
    }
  }

  return (
    <div
      className={isPreview ? 'protection-selector is-preview' : 'protection-selector'}
      data-centred-id={centredPackage?.id}
      data-selected-id={selectedId ?? ''}
      data-slide-count={packages.length}
    >
      <Carousel
        aria-label="Ceramic protection packages"
        className="services-carousel package-carousel-compact"
        opts={carouselOpts}
        setApi={setCarouselApi}
        tabIndex={0}
      >
        <CarouselContent className="protection-carousel-track ml-0">
          {packages.map((item, index) => {
            const placement = carouselPlacement(index, activePackageIndex, packages.length)
            const isActive = placement === 'featured'
            const isSelected = item.id === selectedId

            return (
              <CarouselItem className="protection-carousel-slide pl-0 basis-auto" key={item.id}>
                <article
                  aria-current={isSelected ? 'true' : undefined}
                  aria-hidden={placement === 'hidden' ? true : undefined}
                  className={[
                    'overlap-package-card',
                    placement === 'featured' ? 'featured' : '',
                    placement === 'left' ? 'left-card' : '',
                    placement === 'right' ? 'right-card' : '',
                    placement === 'hidden' ? 'hidden-card' : '',
                    isSelected ? 'is-selected' : '',
                  ].join(' ')}
                  onClick={() => activatePackageCard(index)}
                >
                  <span>{item.badge}</span>
                  <h3>{item.displayName}</h3>
                  <p className="package-product">{item.productName}</p>
                  <p className="package-description">{item.description}</p>
                  <div className="package-price">
                    <small>From</small>
                    <strong>{formatCurrencyFromCents(item.startingPriceCents)}</strong>
                    <small>inc. GST</small>
                  </div>
                  <ul>
                    <li>
                      <CheckCircle2 size={16} aria-hidden="true" />
                      Durability: {item.durabilityLabel}
                    </li>
                    {item.warrantyLabel ? (
                      <li>
                        <CheckCircle2 size={16} aria-hidden="true" />
                        {item.warrantyLabel}
                      </li>
                    ) : null}
                    {item.optionalUpgrade ? (
                      <li>
                        <CheckCircle2 size={16} aria-hidden="true" />
                        {item.optionalUpgrade}
                      </li>
                    ) : null}
                  </ul>
                  <Button
                    aria-describedby={isSelected ? statusId : undefined}
                    aria-label={item.cta}
                    aria-pressed={isSelected}
                    className={isSelected ? 'is-selected-cta' : undefined}
                    onClick={(event) => {
                      event.stopPropagation()
                      selectPackage(item, index)
                    }}
                    tabIndex={isActive ? 0 : -1}
                    type="button"
                    variant={isActive || isSelected ? 'solid' : 'ghost'}
                  >
                    {isSelected ? `Selected · ${item.displayName}` : item.cta}
                  </Button>
                </article>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <div className="carousel-dots" aria-label="Ceramic package carousel progress">
          {packages.map((item, index) => (
            <button
              type="button"
              aria-current={index === activePackageIndex ? true : undefined}
              aria-label={`Show ${item.displayName}`}
              key={item.id}
              onClick={() => showPackageAt(index)}
            >
              <span />
            </button>
          ))}
        </div>
        <div className="protection-carousel-nav" role="group" aria-label="Ceramic package carousel controls">
          <button
            aria-label="Previous protection package"
            className="carousel-arrow carousel-arrow-prev"
            onClick={() =>
              showPackageAt(browseCarousel(activePackageIndex, packages.length, -1))
            }
            type="button"
          >
            <ArrowLeft aria-hidden="true" />
          </button>
          <button
            aria-label="Next protection package"
            className="carousel-arrow carousel-arrow-next"
            onClick={() =>
              showPackageAt(browseCarousel(activePackageIndex, packages.length, 1))
            }
            type="button"
          >
            <ArrowRight aria-hidden="true" />
          </button>
        </div>
      </Carousel>
      {isPreview || (selectedPackage && !continueActions) ? null : selectedPackage && continueActions ? (
        <div className="protection-selection-status" aria-live="polite" id={statusId} role="status">
          <div>
            <p>
              <strong>{selectedPackage.displayName}</strong> selected
            </p>
            <p>
              {selectedPackage.productName} · From{' '}
              {formatCurrencyFromCents(selectedPackage.startingPriceCents)} inc. GST
            </p>
          </div>
          <div className="protection-selection-actions">
            <ButtonLink href={buildProtectionBookHref(toSelectedProtection(selectedPackage))} variant="solid">
              Continue With This Protection
            </ButtonLink>
            <ButtonLink href={buildProtectionQuoteHref(toSelectedProtection(selectedPackage))} variant="ghost">
              Request a Free Quote
            </ButtonLink>
          </div>
        </div>
      ) : (
        <p className="package-guidance-note">
          Select a protection level to continue. Final pricing may vary depending on vehicle size,
          condition and preparation requirements.
        </p>
      )}
    </div>
  )
}
